import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, EmptyState } from "@/components/kit";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarClock, Check, X, Ban, Loader2, Eye } from "lucide-react";

const TABS = [
  { key: "all", label: "All" }, { key: "PENDING_APPROVAL", label: "Pending Approval" },
  { key: "CONFIRMED", label: "Confirmed" }, { key: "REJECTED", label: "Rejected" }, { key: "CANCELLED", label: "Cancelled" },
];

export default function Bookings() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [tab, setTab] = useState("all");
  const [busy, setBusy] = useState(null);
  const [detail, setDetail] = useState(null);
  const canApprove = ["SUPER_ADMIN", "COMPANY_ADMIN", "BRANCH_ADMIN"].includes(user.role);

  const load = () => api.get(`/bookings${tab === "all" ? "" : `?status=${tab}`}`).then((r) => setList(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [tab]);

  const act = async (b, action) => {
    setBusy(`${b.id}-${action}`);
    try { await api.post(`/bookings/${b.id}/${action}`); toast.success("Done"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  if (!list) return <Loader />;

  return (
    <div>
      <PageHeader title="Meeting Room Bookings" subtitle="Confirmed bookings and extended-duration approvals." />
      <Tabs value={tab} onValueChange={setTab} className="mb-5">
        <TabsList className="flex-wrap h-auto">{TABS.map((t) => <TabsTrigger key={t.key} value={t.key} data-testid={`booking-tab-${t.key}`}>{t.label}</TabsTrigger>)}</TabsList>
      </Tabs>

      {list.length === 0 ? <EmptyState icon={CalendarClock} title="No bookings here" /> : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto sf-fade-up">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Room</TableHead><TableHead>Booked By</TableHead><TableHead>Company</TableHead>
              <TableHead>Date</TableHead><TableHead>Time</TableHead><TableHead>Duration</TableHead>
              <TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.map((b) => (
                <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                  <TableCell className="font-medium text-navy-900">{b.room_name}</TableCell>
                  <TableCell>{b.person_name}</TableCell>
                  <TableCell className="text-slate-500">{b.company_name}</TableCell>
                  <TableCell className="text-slate-500">{b.date}</TableCell>
                  <TableCell className="font-mono text-sm">{b.start_time}–{b.end_time}</TableCell>
                  <TableCell>{b.duration_minutes}m</TableCell>
                  <TableCell><StatusBadge status={b.status} /></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => setDetail(b)} data-testid={`view-booking-${b.id}`}><Eye size={14} /></Button>
                      {canApprove && b.status === "PENDING_APPROVAL" && (
                        <>
                          <Button size="sm" onClick={() => act(b, "approve")} disabled={busy} data-testid={`approve-booking-${b.id}`} className="bg-emerald-600 hover:bg-emerald-700"><Check size={14} /></Button>
                          <Button size="sm" variant="outline" onClick={() => act(b, "reject")} disabled={busy} className="border-rose-300 text-rose-700"><X size={14} /></Button>
                        </>
                      )}
                      {["CONFIRMED", "PENDING_APPROVAL"].includes(b.status) && (
                        <Button size="sm" variant="outline" onClick={() => act(b, "cancel")} disabled={busy} data-testid={`cancel-booking-${b.id}`}><Ban size={14} /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="booking-detail-dialog">
          <DialogHeader><DialogTitle>Meeting Details</DialogTitle></DialogHeader>
          {detail && (
            <div>
              <div className="bg-navy-900 text-white rounded-2xl p-5 flex items-center justify-between">
                <div>
                  <h3 className="font-display text-2xl font-bold">{detail.room_name}</h3>
                  <p className="text-blue-200/80 text-sm">{detail.date} · {detail.start_time} – {detail.end_time}</p>
                </div>
                <StatusBadge status={detail.status} />
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 mt-4">
                {[["Meeting Room", detail.room_name], ["Organised By", detail.person_name],
                  ["Email", detail.person_email], ["Phone", detail.phone],
                  ["Cabin", detail.cabin_number], ["Company", detail.company_name], ["Purpose", detail.purpose],
                  ["Date", detail.date], ["Time", `${detail.start_time} – ${detail.end_time}`],
                  ["Duration", `${detail.duration_minutes} min`], ["Approved By", detail.approved_by_name],
                  ["Booked On", detail.created_at ? new Date(detail.created_at).toLocaleString() : "—"]].map(([k, val]) => (
                  <div key={k}>
                    <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{k}</p>
                    <p className="text-sm font-semibold text-navy-900 break-all">{val || "—"}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
                <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-2">Branch Staff (contact)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Branch Admin</p>
                    {(detail.branch_admins || []).length ? detail.branch_admins.map((s, i) => (
                      <p key={i} className="text-sm text-navy-900 font-medium">{s.name} <span className="text-slate-400 font-normal">· {s.email}</span></p>
                    )) : <p className="text-sm text-slate-400">—</p>}
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Receptionist</p>
                    {(detail.receptionists || []).length ? detail.receptionists.map((s, i) => (
                      <p key={i} className="text-sm text-navy-900 font-medium">{s.name} <span className="text-slate-400 font-normal">· {s.email}</span></p>
                    )) : <p className="text-sm text-slate-400">—</p>}
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
